import { _isNotBlank, _notNil } from '@/littledash';
import { prefetchRoute } from '@/routes/prefetchRoute';
import type { CSSProperties, FC, FocusEventHandler, MouseEventHandler, ReactNode } from 'react';
import { Link as ReactLink } from 'react-router-dom';

interface LinkBaseProps extends Partial<Pick<HTMLAnchorElement, 'target' | 'rel'>> {
  className?: string;
  'data-testid'?: string;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  onFocus?: FocusEventHandler<HTMLAnchorElement>;
  onMouseEnter?: MouseEventHandler<HTMLAnchorElement>;
  disabled?: boolean;
  style?: CSSProperties;
  role?: string;
}

interface LinkProps extends LinkBaseProps {
  to?: string;
  openModal?: boolean;
  openTab?: boolean;
  href?: string | boolean;
  children?: ReactNode;
  prefetch?: 'intent' | 'none';
}

const Link: FC<LinkProps> = ({
  to,
  children,
  openModal,
  openTab,
  href,
  prefetch = 'intent',
  onFocus,
  onMouseEnter,
  ...baseProps
}) => {
  let additionalProps: LinkBaseProps = baseProps;

  if (openTab) {
    additionalProps = { ...additionalProps, rel: 'noopener noreferrer', target: '_blank' };
  }

  const handleIntentPrefetch = () => {
    if (prefetch === 'intent' && _isNotBlank(to)) {
      prefetchRoute(to);
    }
  };

  const handleMouseEnter: MouseEventHandler<HTMLAnchorElement> = (event) => {
    handleIntentPrefetch();
    onMouseEnter?.(event);
  };

  const handleFocus: FocusEventHandler<HTMLAnchorElement> = (event) => {
    handleIntentPrefetch();
    onFocus?.(event);
  };

  // TODO: openModal is simply a styling property, onClick seems to handle the actually functionality
  // Modal should really be broken out to another component, as this Link comp essentially has two
  // completely separate functionalities, hence the plethora of optional params
  if (openModal) {
    return (
      <a onMouseEnter={onMouseEnter} onFocus={onFocus} {...additionalProps}>
        {children}
      </a>
    );
  }

  const hasExternalTarget = _isNotBlank(to) && (to.includes('mailto') || to.includes('http'));
  const shouldRenderAnchor = hasExternalTarget || href === true || (typeof href === 'string' && _isNotBlank(href));
  if (shouldRenderAnchor) {
    const anchorHref = typeof href === 'string' ? href : to;
    return (
      <a href={anchorHref} onMouseEnter={onMouseEnter} onFocus={onFocus} {...additionalProps}>
        {children}
      </a>
    );
  }

  return (
    // Blank internal targets fall back to the current location until modal links are split into a dedicated component.
    <ReactLink to={to ?? ''} onMouseEnter={handleMouseEnter} onFocus={handleFocus} {...additionalProps}>
      {children}
    </ReactLink>
  );
};

export default Link;
